import type { useTranslations } from "next-intl";
import { splitSuffixedCode } from "@/lib/i18n/translateBackendError";

/**
 * PB-CDP (plan witty-munching-quilt Rev. 5.3, "Cockpit") — the words for a
 * dissolve: an organisation deleted outright, a solo owner's account taking
 * its organisation with it, a solo owner switching organisations.
 *
 * After the wave only a plan Stripe still bills refuses a dissolve; one-time
 * event plans and purchased balances end with it, without a refund. Three
 * places say so — the organisation page's danger zone and its dialog (F1),
 * the account dialog (F2), the bell's join dialog (F4, F-B) — and they read
 * the verdict from the same two sources:
 *
 *   - a preflight's keys `{dissolveCheckFailed, dissolveBlock, dissolveLosses}`
 *     (`GET /subscriptions/{id}/dissolve-preflight`, the account and join
 *     preflights' dissolve branch) — {@link describeDissolve};
 *   - a door's 400 `activeSubscription:<REASON>[:<epochSec>]`, which reaches
 *     the cockpit as `{message: "activeSubscription", detail:
 *     "activeSubscription:CANCELLING:1793404800"}` (rethrowApi /
 *     handleErrorServer keep the envelope's raw message as `detail`) —
 *     {@link reasonTextFromError}. A Stripe-only verdict shows up only here.
 *
 * Every translator argument is `useTranslations("userSettings")` — the keys
 * live under `userSettings.dissolveBlock.<register>.*` and
 * `userSettings.dissolveLosses.*` (frozen list: company-delete-twin-work
 * `fe-keys.md`).
 */

type Translator = ReturnType<typeof useTranslations>;

/**
 * Whose words: `company` — the organisation is deleted (F1 company card, its
 * dialog and OTP step); `account` — the caller's account goes and takes a solo
 * owner's organisation with it (F1 card of a non-COMPANY customer, F2, their
 * OTP step); `switch` — the bell's join dialog, which dissolves the solo
 * owner's organisation on accept (F4).
 */
export type DissolveRegister = "company" | "account" | "switch";

/** `{reason, untilEpochSec}` as the backend's `DissolveBlock.toMap()` writes it. */
export type DissolveBlock = {
  /** `RUNNING` | `UNKNOWN` | `CANCELLING` today; any other string is a reason this build does not know. */
  reason: string;
  /** Unix SECONDS; set exactly for `CANCELLING`. */
  untilEpochSec?: number | null;
};

/** `{paidPlanEvents, prepaidBalances}` as the backend's `DissolveLosses.toMap()` writes it. */
export type DissolveLosses = {
  paidPlanEvents: number;
  prepaidBalances: boolean;
};

/**
 * The three keys a new backend adds to a dissolve preflight. Nulls are on the
 * wire (the backend serialises maps): `dissolveBlock: null` = not blocked,
 * `dissolveLosses: null` = its read failed. `dissolveBlock` absent is read as
 * null too.
 */
export type DissolvePreflightKeys = {
  dissolveCheckFailed: boolean;
  dissolveBlock?: DissolveBlock | null;
  dissolveLosses?: DissolveLosses | null;
};

/**
 * The new-backend marker: `dissolveCheckFailed` is a boolean. An old backend
 * (no gate keys at all) answers without it, and every reader then keeps
 * today's behaviour exactly.
 */
export function hasDissolveMarker(value: unknown): value is DissolvePreflightKeys {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as { dissolveCheckFailed?: unknown }).dissolveCheckFailed === "boolean"
  );
}

/**
 * `untilEpochSec` as the long date of `locale`, or null when it is not a
 * usable instant. Serbian is formatted in Latin script (`sr-Latn-RS`): the
 * bundle is Latin, and plain `sr-RS` prints Cyrillic month names into it.
 */
export function formatDissolveDate(untilEpochSec: unknown, locale: string): string | null {
  if (typeof untilEpochSec !== "number" || !Number.isFinite(untilEpochSec) || untilEpochSec <= 0) {
    return null;
  }
  const date = new Date(untilEpochSec * 1000);
  if (Number.isNaN(date.getTime())) return null;
  const dateLocale = locale === "sr" || locale.startsWith("sr-") ? "sr-Latn-RS" : locale;
  try {
    return new Intl.DateTimeFormat(dateLocale, { dateStyle: "long" }).format(date);
  } catch {
    return new Intl.DateTimeFormat(undefined, { dateStyle: "long" }).format(date);
  }
}

/**
 * The sentence for a block reason in `register`'s words.
 *
 * `RUNNING` → cancel under Subscription, then delete (no date: the plan runs
 * on). `CANCELLING` → runs until {date}; without a usable date it is the
 * honest `unknown` (the backend never sends that, `DissolveBlock` refuses
 * it). `UNKNOWN` → support. Any other reason — one a later backend adds — →
 * `other`, still a blocking support sentence: never "you may delete".
 *
 * `locale` = `useLocale()`; it only formats the date.
 */
export function reasonText(
  t: Translator,
  reason: unknown,
  register: DissolveRegister,
  untilEpochSec: unknown,
  locale: string,
): string {
  switch (reason) {
    case "RUNNING":
      return t(`dissolveBlock.${register}.running`);
    case "CANCELLING": {
      const date = formatDissolveDate(untilEpochSec, locale);
      return date
        ? t(`dissolveBlock.${register}.cancelling`, { date })
        : t(`dissolveBlock.${register}.unknown`);
    }
    case "UNKNOWN":
      return t(`dissolveBlock.${register}.unknown`);
    default:
      return t(`dissolveBlock.${register}.other`);
  }
}

/** The bare door code of a dissolve block (`DissolveGateErrors.ACTIVE_SUBSCRIPTION`). */
const ACTIVE_SUBSCRIPTION = "activeSubscription";

/**
 * The block a door's refusal carries, or null when `error` is not an
 * `activeSubscription` WITH a reason — anything else (another code, the bare
 * `activeSubscription` of an old backend, of the termination or of an event's
 * legacy row) is for `translateBackendError`.
 *
 * Reads `detail` = the envelope's raw message `activeSubscription:<suffix>`
 * (`splitSuffixedCode`), suffix `RUNNING` | `UNKNOWN` | `CANCELLING:<epochSec>`.
 */
export function dissolveBlockFromError(error: unknown): DissolveBlock | null {
  if (!error || typeof error !== "object") return null;
  const { message, detail } = error as { message?: unknown; detail?: unknown };
  if (message !== ACTIVE_SUBSCRIPTION || typeof detail !== "string") return null;
  const suffixed = splitSuffixedCode(detail);
  if (!suffixed || suffixed.code !== ACTIVE_SUBSCRIPTION) return null;
  const [reason = "", until, ...rest] = suffixed.suffix.split(":");
  const untilEpochSec =
    until !== undefined && rest.length === 0 && /^\d+$/.test(until) ? Number(until) : null;
  return { reason, untilEpochSec };
}

/**
 * {@link reasonText} for a door's 400 `activeSubscription:<…>`, or null when
 * the error is not one ({@link dissolveBlockFromError}) — then the caller
 * falls back to `translateBackendError` as before.
 */
export function reasonTextFromError(
  t: Translator,
  error: unknown,
  register: DissolveRegister,
  locale: string,
): string | null {
  const block = dissolveBlockFromError(error);
  return block ? reasonText(t, block.reason, register, block.untilEpochSec, locale) : null;
}

function isLosses(value: unknown): value is DissolveLosses {
  return (
    !!value &&
    typeof value === "object" &&
    typeof (value as { paidPlanEvents?: unknown }).paidPlanEvents === "number" &&
    typeof (value as { prepaidBalances?: unknown }).prepaidBalances === "boolean"
  );
}

/** `shown` of the generic notice (backend shape `P\d{1,9}B[01]|GENERIC`). */
const GENERIC_SHOWN = "GENERIC";

/**
 * A paid-plan count the specific sentence can name and its token can carry:
 * a whole number 0 … 999 999 999 (the backend's `P\d{1,9}`). The backend
 * sends whole numbers; anything else (1.5, -1, 1e21) is unreadable → the
 * generic notice (audit r1, LOW-4).
 */
function isCount(value: number): boolean {
  return Number.isInteger(value) && value >= 0 && value <= 999_999_999;
}

/**
 * The loss notice of a dissolve that may go ahead and which sentence it is,
 * or null for none — ONE branch picks both, so the token the acknowledgement
 * records (`shown`) can never name a sentence the dialog did not show.
 *
 * Composed in this order, one space apart: the paid-plan events (ICU plural,
 * only when there are any) → the prepaid balances (only when true) → "no
 * refund" (whenever one of the two shows) — token `P<n>B<0|1>`. Neither → no
 * notice. Losses the backend could not read (`dissolveLosses` null or a
 * count that is not a whole number ≥ 0) or a failed check — even one that
 * came with real losses — → the generic notice
 * ALONE, token `GENERIC`: it already says "no refund", and not knowing is no
 * reason to say nothing. An old backend (no marker) → null: today's dialog.
 */
function lossesNotice(t: Translator, keys: unknown): { text: string; shown: string } | null {
  if (!hasDissolveMarker(keys)) return null;
  const generic = () => ({ text: t("dissolveLosses.generic"), shown: GENERIC_SHOWN });
  if (keys.dissolveCheckFailed) return generic();
  const losses = keys.dissolveLosses;
  if (!isLosses(losses) || !isCount(losses.paidPlanEvents)) return generic();
  const parts: string[] = [];
  const paidPlanEvents = losses.paidPlanEvents;
  if (paidPlanEvents > 0) {
    parts.push(t("dissolveLosses.paidPlanEvents", { count: paidPlanEvents }));
  }
  if (losses.prepaidBalances) parts.push(t("dissolveLosses.prepaidBalances"));
  if (parts.length === 0) return null;
  parts.push(t("dissolveLosses.noRefund"));
  return {
    text: parts.join(" "),
    shown: `P${paidPlanEvents}B${losses.prepaidBalances ? 1 : 0}`,
  };
}

/** The loss notice of a dissolve that may go ahead, or null for none ({@link lossesNotice}). */
export function lossesNoticeText(t: Translator, keys: unknown): string | null {
  return lossesNotice(t, keys)?.text ?? null;
}

/** The loss notice, its token and the flag — {@link describeLosses}. */
export type LossesDescription = Pick<DissolveDescription, "lossesNotice" | "shown" | "acknowledgeLosses">;

/**
 * PB-CDP-8 — the termination dialog's loss notice: {@link lossesNotice} on the
 * same keys, WITHOUT looking at `dissolveBlock`. The owner can only end the
 * contract while the plan is cancelled but still billing (a CANCELLING block
 * for the three dissolving doors, where {@link describeDissolve} shows no
 * notice) or without one; the dissolution at contract end forfeits the same
 * losses, so the tick is asked here anyway. A failed check → the generic
 * notice (`GENERIC`), no keys → none.
 */
export function describeLosses(t: Translator, keys: unknown): LossesDescription {
  const notice = lossesNotice(t, keys);
  return {
    lossesNotice: notice?.text ?? null,
    shown: notice?.shown ?? null,
    acknowledgeLosses: notice !== null,
  };
}

/** What a dissolve dialog shows, and the flag its requests carry. */
export type DissolveDescription = {
  /** A new backend answered (the marker); false = today's behaviour, nothing below is set. */
  isNewBackend: boolean;
  /** The block reason in the register's words — the dissolve cannot go ahead. */
  blockText: string | null;
  /** The loss notice — only when not blocked. */
  lossesNotice: string | null;
  /**
   * PB-CDP-7: which notice `lossesNotice` is — `P<n>B<0|1>` (the specific
   * sentence: n paid-plan events, B1 = the balances sentence) or `GENERIC`;
   * null exactly when there is no notice. The acknowledgement records it
   * (`lossesAck(locale, shown)`).
   */
  shown: string | null;
  /**
   * `acknowledgeLosses=true` on the request AND the confirm — exactly when a
   * loss notice (specific or generic) was shown, and (PB-CDP-7) only once the
   * user ticked the box under it. The doors refuse a dissolve with losses
   * without it (`lossesNotAcknowledged`).
   */
  acknowledgeLosses: boolean;
};

/**
 * One dissolve preflight's keys, read for a dialog (F1 dialog, F2, F4).
 *
 * Old backend → nothing (today's dialog). `dissolveBlock` set → the reason,
 * no notice, no flag. Otherwise → {@link lossesNotice}: a failed check
 * reads as the generic notice and lets the request go — the door fails
 * closed on its own. (The F1 card does not open its dialog on a failed check:
 * it reads `dissolveCheckFailed` itself first.)
 */
export function describeDissolve(
  t: Translator,
  keys: unknown,
  register: DissolveRegister,
  locale: string,
): DissolveDescription {
  if (!hasDissolveMarker(keys)) {
    return { isNewBackend: false, blockText: null, lossesNotice: null, shown: null, acknowledgeLosses: false };
  }
  const block = keys.dissolveBlock;
  if (block != null) {
    const { reason, untilEpochSec } =
      typeof block === "object" ? block : { reason: undefined, untilEpochSec: undefined };
    return {
      isNewBackend: true,
      blockText: reasonText(t, reason, register, untilEpochSec, locale),
      lossesNotice: null,
      shown: null,
      acknowledgeLosses: false,
    };
  }
  const notice = lossesNotice(t, keys);
  return {
    isNewBackend: true,
    blockText: null,
    lossesNotice: notice?.text ?? null,
    shown: notice?.shown ?? null,
    acknowledgeLosses: notice !== null,
  };
}
